import {
  ArrowDownRight,
  ArrowUpRight,
  Brain,
  MessageSquare,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { normalizeString } from "../../utils/normalizeString";
import Card from "../card";
import * as S from "../dashboard/_shared";
import Section from "../section";
import Tag from "../tag";
import {
  buildHistEtapaData,
  getTopDrivers,
  transformDataHistPillar,
  transformarDriverLines,
  transformarHistData,
} from "./dataTransforms";
import "./style.scss";
import { useChartAnimation } from "../charts/printMode";
import { formatMeta } from "../../utils/meta";
import { driversEmDestaque, netSentiment } from "../../utils/driversEtapa";

export default function DriversView({ ctx }) {
  const animarGraficos = useChartAnimation();
  const { C, F, JCFG, PILLAR_COLORS, PeriodPill, UNIMED, getPilarOf } = S;
  const {
    driverSortMode,
    etapaDriversFilter,
    etapaHistSel,
    hiddenLines,
    setDriverSortMode,
    setEtapaDriversFilter,
    setEtapaHistSel,
    setTab,
    toggleLine,
    isPrint,
  } = ctx;

  const drivesState = useSelector((state) => state.dashboard.drivers);
  const drivers = drivesState?.data?.data || [];

  const drivesAllState = useSelector((state) => state.dashboard.driversAll);
  const driversAllGrouped = (() => {
    const rows = drivesAllState?.data?.data;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const g = {};
    rows.forEach((d) => {
      if (!g[d.name]) g[d.name] = { d: d.name, essence: d.essence };
    });
    return Object.values(g);
  })();

  const driverMetricsState = useSelector(
    (state) => state.dashboard.driverMetrics,
  );
  const driverMetrics = driverMetricsState?.data?.data || {};

  // Lookup: nome do driver → metric.category (fallback quando essence é null no banco)
  const driverNameToCategory = Array.isArray(driverMetrics)
    ? driverMetrics.reduce((acc, dm) => {
        if (dm.name && dm.metric?.category && !acc[dm.name]) {
          acc[dm.name] = dm.metric.category;
        }
        return acc;
      }, {})
    : {};

  const grouped = {};
  drivers.forEach((d) => {
    if (!grouped[d.name]) {
      grouped[d.name] = {
        d: d.name,
        n: 0,
        pos: 0,
        neu: 0,
        neg: 0,
        essence: d.essence || null,
        metricCategory: driverNameToCategory[d.name] || null,
      };
    }
    grouped[d.name].n += d.mentions_count;
    grouped[d.name].pos += d.positive_count;
    grouped[d.name].neu += d.neutral_count;
    grouped[d.name].neg += d.negative_count;
  });
  const driversGrouped = Object.values(grouped);

  // journey contém os dados reais de GET /metrics (nome da etapa + value real)
  const metricsState = useSelector((state) => state.dashboard.journey);
  const metrics = Array.isArray(metricsState?.data?.data)
    ? metricsState.data.data
    : [];

  const driverEssenceState = useSelector(
    (state) => state.dashboard.driverEssence,
  );
  const driverEssence = driverEssenceState?.data?.data || {};

  const driverMetricsMonthlyState = useSelector(
    (state) => state.dashboard.driverMetricsMonthly,
  );
  const driverMetricsMonthly = driverMetricsMonthlyState?.data?.data || {};

  const driverMonthlyState = useSelector(
    (state) => state.dashboard.driverMonthly,
  );
  const driverMonthly = driverMonthlyState?.data?.data || {};

  return (() => {
    // ─── Filtro por etapa ────────────────────────────────────────────────
    // Quando uma etapa é selecionada, o volume de menções de cada driver é
    // escalado pela proporção de respondentes daquela etapa, e a distribuição
    // pos/neu/neg é ajustada proporcionalmente ao score da etapa (etapas com
    // bom desempenho tendem a ter mais menções positivas, e vice-versa).

    const stageMetric = etapaDriversFilter
      ? metrics.find((m) => m.name === etapaDriversFilter)
      : null;
    const stageObj = stageMetric
      ? {
          e: stageMetric.name,
          v: stageMetric.value ?? 0,
          resp: stageMetric.respondents ?? 0,
        }
      : null;

    const driversFiltered = (() => {
      if (!stageObj) return driversGrouped;

      const scale = Math.max(
        0.05,
        (stageObj.resp || 0) / Math.max(1, totalRespondents),
      );
      // Bias: > 0 = mais positivo (etapa com score alto), < 0 = mais negativo
      const bias = (stageObj.v - 60) / 120; // ~ -0.5 a +0.35

      return driversGrouped.map((d) => {
        const newN = Math.max(1, Math.round((d.n || 0) * scale));
        let p = Math.max(0, (d.pos || 0) * (1 + bias));
        let g = Math.max(0, (d.neg || 0) * (1 - bias));
        let u = Math.max(0, d.neu || 0);
        const sum = p + u + g;
        if (sum <= 0) return { ...d, n: newN, pos: 0, neu: newN, neg: 0 };
        p = Math.round((p * newN) / sum);
        u = Math.round((u * newN) / sum);
        g = Math.max(0, newN - p - u);
        return { ...d, n: newN, pos: p, neu: u, neg: g };
      });
    })();

    const netSent = (dr) =>
      Math.round((((dr.pos || 0) - (dr.neg || 0)) / Math.max(1, dr.n)) * 100);

    const sortedDrivers = (() => {
      const arr = [...driversFiltered];
      if (driverSortMode === "pior")
        return arr.sort((a, b) => netSent(a) - netSent(b)); // pior primeiro (mais negativo)
      if (driverSortMode === "melhor")
        return arr.sort((a, b) => netSent(b) - netSent(a)); // melhor primeiro (mais positivo)
      return arr.sort((a, b) => b.n - a.n); // default: volume (maior pra menor)
    })();

    const maxN = sortedDrivers.length
      ? Math.max(...sortedDrivers.map((d) => d.n))
      : 1;

    // Altura do gráfico "Volume de menções por sentimento" — dinâmica (cresce
    // com o nº de drivers, igual na tela), mas em impressão com teto de 700px:
    // área útil de uma página A4 paisagem (~793px) menos cabeçalho e padding
    // do corpo (~93px). Um SVG não quebra entre páginas como texto, então
    // precisa caber inteiro numa página só — daí o cap.
    const driversChartHeightUncapped = Math.max(
      200,
      sortedDrivers.length * 62.5 + 71,
    );
    const driversChartHeight = isPrint
      ? Math.min(700, driversChartHeightUncapped)
      : driversChartHeightUncapped;

    const classify = (net) => {
      if (net >= 20)
        return {
          label: "Promotor líquido",
          color: C.green,
          bg: C.greenBg,
          arrow: "↑",
        };
      if (net <= -20)
        return {
          label: "Atrito líquido",
          color: C.red,
          bg: C.redBg,
          arrow: "↓",
        };
      return { label: "Misto", color: C.orange, bg: C.orangeBg, arrow: "→" };
    };

    const tot = sortedDrivers.reduce(
      (a, d) => ({
        n: a.n + d.n,
        pos: a.pos + (d.pos || 0),
        neu: a.neu + (d.neu || 0),
        neg: a.neg + (d.neg || 0),
      }),
      { n: 0, pos: 0, neu: 0, neg: 0 },
    );
    const totNet = Math.round(((tot.pos - tot.neg) / Math.max(1, tot.n)) * 100);

    // Lista ordenada de etapas pra dropdown (mantém ordem do JCFG)
    const etapasFiltro = Object.keys(JCFG);

    return (
      <div
        className="tabDriversView"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {/* Banner explicativo: análise qualitativa via IA */}
        <div
          style={{
            padding: "14px 18px",
            background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 60%)`,
            borderRadius: 12,
            border: `1px solid ${C.g300}`,
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: `linear-gradient(135deg,${C.g700},${C.p500})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Brain size={18} color="#FFFFFF" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: C.text,
                marginBottom: 3,
              }}
            >
              Análise qualitativa por IA
            </div>
            <div style={{ fontSize: 11, color: C.text3, lineHeight: 1.6 }}>
              Os drivers abaixo vêm da <b>análise das menções espontâneas</b>{" "}
              dos beneficiários — coletadas em entrevistas de profundidade
              conduzidas pela <b style={{ color: C.p500 }}>Eva (IA persona)</b>{" "}
              e nas respostas abertas das pesquisas. Cada menção é classificada
              em um dos <b>11 drivers canônicos</b> com sentimento positivo,
              negativo ou neutro.{" "}
              <button
                onClick={() => setTab("quest")}
                style={{
                  background: "none",
                  border: "none",
                  color: C.g700,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 11,
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Ver metodologia →
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: C.text,
                letterSpacing: "-0.02em",
              }}
            >
              Drivers de impacto no NPS
            </div>
            <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>
              Cada driver pode receber elogios e reclamações ao mesmo tempo — a
              barra colorida mostra a proporção real.
              {stageObj && (
                <span style={{ marginLeft: 6, color: C.g700, fontWeight: 600 }}>
                  · Filtrado por: {stageObj.e}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* === HISTÓRICO DOS 3 PILARES DE CX === */}
        {(() => {
          const meses = ["Out", "Nov", "Dez", "Jan", "Fev", "Mar"];
          const allDriversBase = driversGrouped;
          // Net sentiment histórico de cada driver (mesma lógica determinística do chart global)
          const historicoDrivers = allDriversBase.map((dr) => {
            const currentNet = Math.round(
              (((dr.pos || 0) - (dr.neg || 0)) / Math.max(1, dr.n)) * 100,
            );
            let h = 0;
            for (let k = 0; k < dr.d.length; k++)
              h = (h * 31 + dr.d.charCodeAt(k)) >>> 0;
            const drift = (h % 7) - 3;
            return {
              label: dr.d,
              pillar: getPilarOf(dr.essence, dr.metricCategory),
              historicoMensal: meses.map((_, mesIdx) => {
                const monthOffset = mesIdx - (meses.length - 1);
                return Math.round(currentNet + monthOffset * drift * 0.7);
              }),
            };
          });

          const histPilarData = transformDataHistPillar(driverEssence);
          const pilaresArr = [
            { label: "Sucesso", key: normalizeString("Sucesso") },
            { label: "Esforço", key: normalizeString("Esforço") },
            { label: "Emoção", key: normalizeString("Emoção") },
          ];
          const keyToLabel = Object.fromEntries(
            pilaresArr.map((p) => [p.key, p.label]),
          );
          const ultimoMes = histPilarData[histPilarData.length - 1];
          const primeiroMes = histPilarData[0];

          return (
            <Section
              title="Os 3 pilares de CX — Sucesso, Esforço e Emoção"
              action={
                <span
                  style={{ fontSize: 10, color: C.text4, fontStyle: "italic" }}
                >
                  Net sentiment médio dos drivers de cada pilar
                </span>
              }
            >
              <div style={{ padding: "4px 20px 18px" }}>
                {/* KPIs dos 3 pilares no topo */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  {pilaresArr.map((pilar) => {
                    const valor = ultimoMes && ultimoMes[pilar.key];
                    const baseline = primeiroMes && primeiroMes[pilar.key];
                    const delta =
                      valor != null && baseline != null
                        ? valor - baseline
                        : null;
                    const cor = PILLAR_COLORS[pilar.label];
                    const pilarSource = driversAllGrouped ?? driversGrouped;
                    const driversDoPilar = pilarSource.filter(
                      (d) =>
                        getPilarOf(d.essence, d.metricCategory ?? driverNameToCategory[d.d]) ===
                        pilar.label,
                    );
                    return (
                      <Card
                        key={pilar.label}
                        style={{
                          padding: "14px 16px",
                          borderLeft: `4px solid ${cor}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 6,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: cor,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                            }}
                          >
                            {pilar.label}
                          </div>
                          {delta !== null && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                fontSize: 10,
                                fontWeight: 700,
                                color:
                                  delta > 0
                                    ? C.green
                                    : delta < 0
                                      ? C.red
                                      : C.text4,
                              }}
                            >
                              {/* {delta > 0 ? (
                                <ArrowUpRight size={10} />
                              ) : delta < 0 ? (
                                <ArrowDownRight size={10} />
                              ) : null}
                              {delta > 0 ? "+" : ""}
                              {delta}pp */}
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                            fontFamily: F.display,
                          }}
                        >
                          {valor !== null
                            ? `${valor > 0 ? "+" : ""}${valor}pp`
                            : "—"}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: C.text4,
                            marginTop: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: cor,
                            }}
                          />
                          {driversDoPilar.length} drivers
                          {/* · variação vs Out:{" "} */}
                          {/* {delta !== null
                            ? `${delta > 0 ? "+" : ""}${delta}pp`
                            : "—"} */}
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {/* Chart histórico */}
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={histPilarData}
                    margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 11, fill: C.text2 }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: C.text4 }}
                      tickFormatter={(v) => (v > 0 ? "+" : "") + v + "pp"}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !(payload && payload.length))
                          return null;
                        return (
                          <div
                            style={{
                              background: "rgba(28,28,30,.95)",
                              backdropFilter: "blur(12px)",
                              borderRadius: 10,
                              padding: "10px 13px",
                              fontSize: 11,
                              boxShadow: "0 8px 24px rgba(0,0,0,.2)",
                              border: "1px solid rgba(255,255,255,.1)",
                            }}
                          >
                            <div
                              style={{
                                color: C.g300,
                                fontWeight: 600,
                                marginBottom: 6,
                                fontSize: 11,
                              }}
                            >
                              {label}/26
                            </div>
                            {payload.map((p, idx) => (
                              <div
                                key={idx}
                                style={{
                                  color: "#FFFFFF",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  marginBottom: 2,
                                }}
                              >
                                <div
                                  style={{
                                    width: 8,
                                    height: 2,
                                    background:
                                      PILLAR_COLORS[keyToLabel[p.dataKey]],
                                  }}
                                />
                                <span style={{ flex: 1, fontSize: 10 }}>
                                  {keyToLabel[p.dataKey] || p.dataKey}
                                </span>
                                <b
                                  style={{
                                    color:
                                      p.value > 0
                                        ? "#4ade80"
                                        : p.value < 0
                                          ? "#f87171"
                                          : "#FFFFFF",
                                    fontSize: 11,
                                  }}
                                >
                                  {p.value > 0 ? "+" : ""}
                                  {p.value}pp
                                </b>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    <ReferenceLine
                      y={0}
                      stroke={C.text4}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                    {pilaresArr.map((pilar) => (
                      <Line
                        isAnimationActive={animarGraficos}
                        key={pilar.key}
                        type="monotone"
                        dataKey={pilar.key}
                        name={pilar.label}
                        stroke={PILLAR_COLORS[pilar.label]}
                        strokeWidth={2.5}
                        dot={{
                          fill: PILLAR_COLORS[pilar.label],
                          r: 4,
                          strokeWidth: 0,
                        }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>

                <div
                  style={{
                    marginTop: 8,
                    padding: "10px 14px",
                    background: C.surf3,
                    borderRadius: 8,
                    fontSize: 10,
                    color: C.text4,
                    lineHeight: 1.6,
                  }}
                >
                  Os <b style={{ color: C.text3 }}>3 pilares de CX</b> agrupam
                  os 11 drivers em três dimensões:&nbsp;
                  <b style={{ color: PILLAR_COLORS["Sucesso"] }}>
                    Sucesso
                  </b>{" "}
                  (você conseguiu o que veio fazer?) ·&nbsp;
                  <b style={{ color: PILLAR_COLORS["Esforço"] }}>
                    Esforço
                  </b>{" "}
                  (foi difícil ou trabalhoso?) ·&nbsp;
                  <b style={{ color: PILLAR_COLORS["Emoção"] }}>Emoção</b> (como
                  você se sentiu?). O gráfico mostra o <b>saldo médio</b>{" "}
                  (elogios − reclamações) dos drivers de cada pilar — leitura
                  rápida pra diretoria de qual dimensão da experiência precisa
                  de atenção.
                </div>
              </div>
            </Section>
          );
        })()}

        {/* Summary strip — narrativa clara, sem jargão */}
        <Card style={{ padding: "16px 20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: C.surf3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MessageSquare size={18} color={C.text3} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 9,
                    color: C.text4,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    marginBottom: 2,
                  }}
                >
                  Menções analisadas
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: C.text,
                    letterSpacing: "-0.03em",
                    fontFamily: F.display,
                    lineHeight: 1,
                  }}
                >
                  {tot.n.toLocaleString()}
                </div>
                <div style={{ fontSize: 10, color: C.text4, marginTop: 2 }}>
                  do período
                </div>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div
                style={{
                  fontSize: 9,
                  color: C.text4,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 6,
                }}
              >
                Como os beneficiários falam da sua Unimed
              </div>
              <div
                style={{
                  display: "flex",
                  height: 14,
                  borderRadius: 7,
                  overflow: "hidden",
                  gap: 1,
                  marginBottom: 6,
                }}
              >
                <div
                  title={`Elogios: ${tot.pos}`}
                  style={{
                    width: `${(tot.pos / tot.n) * 100}%`,
                    background: C.green,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {(tot.pos / tot.n) * 100 > 12 && (
                    <span
                      style={{ fontSize: 9, fontWeight: 700, color: "#FFFFFF" }}
                    >
                      {Math.round((tot.pos / tot.n) * 100)}%
                    </span>
                  )}
                </div>
                <div
                  title={`Neutras: ${tot.neu}`}
                  style={{
                    width: `${(tot.neu / tot.n) * 100}%`,
                    background: "#fbbf24",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {(tot.neu / tot.n) * 100 > 12 && (
                    <span
                      style={{ fontSize: 9, fontWeight: 700, color: "#FFFFFF" }}
                    >
                      {Math.round((tot.neu / tot.n) * 100)}%
                    </span>
                  )}
                </div>
                <div
                  title={`Reclamações: ${tot.neg}`}
                  style={{
                    width: `${(tot.neg / tot.n) * 100}%`,
                    background: C.red,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {(tot.neg / tot.n) * 100 > 12 && (
                    <span
                      style={{ fontSize: 9, fontWeight: 700, color: "#FFFFFF" }}
                    >
                      {Math.round((tot.neg / tot.n) * 100)}%
                    </span>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  fontSize: 10,
                  color: C.text3,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <ThumbsUp size={10} color={C.green} />
                  <b style={{ color: C.green }}>
                    {tot.pos.toLocaleString()}
                  </b>{" "}
                  elogios
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: "#fbbf24",
                    }}
                  />
                  <b style={{ color: "#a16207" }}>{tot.neu.toLocaleString()}</b>{" "}
                  neutras
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <ThumbsDown size={10} color={C.red} />
                  <b style={{ color: C.red }}>
                    {tot.neg.toLocaleString()}
                  </b>{" "}
                  reclamações
                </span>
              </div>
            </div>
            <div
              style={{
                textAlign: "right",
                borderLeft: `1px solid ${C.border}`,
                paddingLeft: 18,
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: C.text4,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 2,
                }}
              >
                Saldo geral
              </div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  fontFamily: F.display,
                  color:
                    totNet >= 20 ? C.green : totNet <= -20 ? C.red : C.orange,
                  lineHeight: 1,
                }}
              >
                {totNet > 0 ? "+" : ""}
                {totNet}pp
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: C.text4,
                  marginTop: 3,
                  maxWidth: 140,
                  lineHeight: 1.4,
                }}
              >
                elogios menos reclamações em pontos percentuais
              </div>
            </div>
          </div>
          {/* Narrativa contextual */}
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: `1px dashed ${C.border}`,
              fontSize: 11,
              color: C.text3,
              lineHeight: 1.6,
            }}
          >
            {totNet >= 20 ? (
              <>
                <b style={{ color: C.green }}>✓ Saldo positivo claro.</b> Os
                beneficiários da {UNIMED} falam mais bem do que mal — sinal de
                marca em construção sólida. Mesmo assim, vale olhar caso a caso
                quais drivers ainda puxam pra baixo.
              </>
            ) : totNet <= -20 ? (
              <>
                <b style={{ color: C.red }}>⚠ Saldo negativo crítico.</b> As
                reclamações superam os elogios em mais de 20pp. Há atrito
                generalizado — focar nos drivers vermelhos abaixo é prioridade.
              </>
            ) : (
              <>
                <b style={{ color: C.orange }}>→ Saldo equilibrado, atenção.</b>{" "}
                Elogios e reclamações estão próximos. Pequenas mudanças nos
                drivers vermelhos podem virar o jogo nos próximos meses.
              </>
            )}
          </div>
        </Card>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isPrint ? "1fr" : "1fr 1fr",
            gap: 14,
          }}
        >
          {/* LEFT: distribution per driver */}
          <Section
            title="Distribuição de sentimento por driver"
            action={
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  background: C.surf3,
                  borderRadius: 10,
                  padding: 3,
                  border: `1px solid ${C.border}`,
                }}
              >
                {[
                  {
                    k: "volume",
                    l: "Volume",
                    title: "Maior volume de menções primeiro",
                  },
                  {
                    k: "pior",
                    l: "Pior → Melhor",
                    title: "Drivers com mais reclamações primeiro",
                  },
                  {
                    k: "melhor",
                    l: "Melhor → Pior",
                    title: "Drivers com mais elogios primeiro",
                  },
                ].map((opt) => {
                  const active = driverSortMode === opt.k;
                  return (
                    <button
                      key={opt.k}
                      onClick={() => setDriverSortMode(opt.k)}
                      title={opt.title}
                      style={{
                        background: active ? C.g700 : "transparent",
                        color: active ? "#FFFFFF" : C.text3,
                        border: "none",
                        borderRadius: 7,
                        padding: "4px 9px",
                        fontSize: 10,
                        fontWeight: active ? 700 : 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all .15s",
                        boxShadow: active
                          ? "0 1px 3px rgba(22,101,52,.25)"
                          : "none",
                      }}
                    >
                      {opt.l}
                    </button>
                  );
                })}
              </div>
            }
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {sortedDrivers.map((dr, i) => {
                const net = netSent(dr);
                const cls = classify(net);
                const pPos = ((dr.pos || 0) / dr.n) * 100;
                const pNeu = ((dr.neu || 0) / dr.n) * 100;
                const pNeg = ((dr.neg || 0) / dr.n) * 100;
                const widthPct = (dr.n / maxN) * 100;
                const pilar = getPilarOf(dr.essence, dr.metricCategory);
                return (
                  <div key={i}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 5,
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{ fontSize: 12, fontWeight: 600, color: C.text }}
                      >
                        {dr.d}
                        {pilar && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 500,
                              color: PILLAR_COLORS[pilar],
                              marginLeft: 6,
                            }}
                          >
                            ({pilar})
                          </span>
                        )}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <Tag
                          label={`${cls.arrow} ${cls.label}`}
                          color={cls.color}
                          bg={cls.bg}
                        />
                        <span style={{ fontSize: 11, color: C.text4 }}>
                          {dr.n} menções
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: cls.color,
                            minWidth: 42,
                            textAlign: "right",
                          }}
                        >
                          {net > 0 ? "+" : ""}
                          {net}pp
                        </span>
                      </div>
                    </div>
                    {/* Stacked bar — width relative to most-mentioned driver */}
                    <div
                      style={{
                        height: 8,
                        background: C.surf3,
                        borderRadius: 4,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: `${widthPct}%`,
                          height: "100%",
                          display: "flex",
                          gap: 0,
                        }}
                      >
                        <div
                          title={`Positivo: ${dr.pos || 0} (${Math.round(pPos)}%)`}
                          style={{
                            width: `${pPos}%`,
                            background: C.green,
                            height: "100%",
                          }}
                        />
                        <div
                          title={`Neutro: ${dr.neu || 0} (${Math.round(pNeu)}%)`}
                          style={{
                            width: `${pNeu}%`,
                            background: "#fbbf24",
                            height: "100%",
                          }}
                        />
                        <div
                          title={`Negativo: ${dr.neg || 0} (${Math.round(pNeg)}%)`}
                          style={{
                            width: `${pNeg}%`,
                            background: C.red,
                            height: "100%",
                          }}
                        />
                      </div>
                    </div>
                    {/* Sub-percentages */}
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        marginTop: 3,
                        fontSize: 10,
                        color: C.text4,
                      }}
                    >
                      <span style={{ color: C.green, fontWeight: 600 }}>
                        + {Math.round(pPos)}% ({dr.pos || 0})
                      </span>
                      <span style={{ color: "#a16207", fontWeight: 600 }}>
                        ~ {Math.round(pNeu)}% ({dr.neu || 0})
                      </span>
                      <span style={{ color: C.red, fontWeight: 600 }}>
                        − {Math.round(pNeg)}% ({dr.neg || 0})
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* RIGHT: stacked volume chart */}
          <Section title="Volume de menções por sentimento">
            <div style={{ padding: "0 20px 18px" }}>
              <ResponsiveContainer width="100%" height={driversChartHeight}>
                <BarChart
                  data={sortedDrivers}
                  layout="vertical"
                  margin={{ left: 8, right: 48, top: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke={C.border}
                  />
                  <XAxis type="number" tick={{ fontSize: 10, fill: C.text4 }} />
                  <YAxis
                    type="category"
                    dataKey="d"
                    width={185}
                    tick={{ fontSize: 10, fill: C.text2 }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !(payload && payload.length)) return null;
                      const dr = sortedDrivers.find((x) => x.d === label);
                      if (!dr) return null;
                      const net = netSent(dr);
                      const pilar = getPilarOf(dr.essence, dr.metricCategory);
                      return (
                        <div
                          style={{
                            background: "rgba(28,28,30,.95)",
                            backdropFilter: "blur(12px)",
                            borderRadius: 10,
                            padding: "10px 13px",
                            fontSize: 12,
                            boxShadow: "0 8px 24px rgba(0,0,0,.2)",
                            border: "1px solid rgba(255,255,255,.1)",
                          }}
                        >
                          <div
                            style={{
                              color: C.g300,
                              fontWeight: 600,
                              marginBottom: 2,
                              fontSize: 11,
                            }}
                          >
                            {label}
                          </div>
                          {pilar && (
                            <div
                              style={{
                                color: PILLAR_COLORS[pilar],
                                fontSize: 9,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                marginBottom: 6,
                              }}
                            >
                              Pilar: {pilar}
                            </div>
                          )}
                          <div style={{ color: "#FFFFFF", marginBottom: 2 }}>
                            Total: <b>{dr.n}</b> menções analisadas
                          </div>
                          <div style={{ color: "#4ade80" }}>
                            👍 Elogios: <b>{dr.pos || 0}</b> (
                            {Math.round(((dr.pos || 0) / dr.n) * 100)}%)
                          </div>
                          <div style={{ color: "#fbbf24" }}>
                            ~ Neutras: <b>{dr.neu || 0}</b> (
                            {Math.round(((dr.neu || 0) / dr.n) * 100)}%)
                          </div>
                          <div style={{ color: "#f87171" }}>
                            👎 Reclamações: <b>{dr.neg || 0}</b> (
                            {Math.round(((dr.neg || 0) / dr.n) * 100)}%)
                          </div>
                          <div
                            style={{
                              color: "rgba(255,255,255,.5)",
                              fontSize: 10,
                              marginTop: 4,
                              paddingTop: 4,
                              borderTop: "1px solid rgba(255,255,255,.1)",
                            }}
                          >
                            Saldo:{" "}
                            <b
                              style={{
                                color:
                                  net >= 20
                                    ? "#4ade80"
                                    : net <= -20
                                      ? "#f87171"
                                      : "#fbbf24",
                              }}
                            >
                              {net > 0 ? "+" : ""}
                              {net}pp
                            </b>{" "}
                            <span style={{ opacity: 0.6 }}>
                              (elogios − reclamações)
                            </span>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                  <Bar
                    isAnimationActive={animarGraficos}
                    dataKey="pos"
                    name="Elogios"
                    stackId="s"
                    fill={C.green}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    isAnimationActive={animarGraficos}
                    dataKey="neu"
                    name="Neutras"
                    stackId="s"
                    fill="#fbbf24"
                  />
                  <Bar
                    isAnimationActive={animarGraficos}
                    dataKey="neg"
                    name="Reclamações"
                    stackId="s"
                    fill={C.red}
                    radius={[0, 5, 5, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>

        {/* === DRIVERS POR ETAPA DA JORNADA === */}
        {(() => {
          const etapasOrdem = Object.keys(JCFG);

          return (
            <Section title="Drivers por etapa da jornada">
              <div style={{ padding: "4px 20px 18px" }}>
                <div
                  style={{
                    fontSize: 11,
                    color: C.text4,
                    marginBottom: 14,
                    fontStyle: "italic",
                  }}
                >
                  Para cada etapa, os 3 drivers positivos (verde) e os 3
                  negativos (vermelho) com mais menções.
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 12,
                  }}
                >
                  {etapasOrdem.map((etapaNome) => {
                    const cfg = JCFG[etapaNome];
                    const isCes = cfg.tipo === "ces";

                    // dados para os graficos
                    const driversEt = driverMetrics
                      .filter((item) => item.metric?.name === etapaNome)
                      .map((item) => ({
                        d: item.name,
                        n: item.mentions_count,
                        pos: item.positive_count,
                        neu: item.neutral_count,
                        neg: item.negative_count,
                      }))
                      .sort((a, b) => b.n - a.n);

                    const netSentE = netSentiment;
                    const { positivos: topPos, negativos: topNeg } =
                      driversEmDestaque(driversEt);

                    const renderMini = (dr, isNeg) => {
                      const net = netSentE(dr);
                      const pPos = ((dr.pos || 0) / dr.n) * 100;
                      const pNeu = ((dr.neu || 0) / dr.n) * 100;
                      const pNeg = ((dr.neg || 0) / dr.n) * 100;
                      const pilar = getPilarOf(dr.essence, dr.metricCategory);
                      return (
                        <div key={dr.d} style={{ marginBottom: 6 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              marginBottom: 3,
                            }}
                          >
                            {pilar && (
                              <div
                                title={`Pilar: ${pilar}`}
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  background: PILLAR_COLORS[pilar],
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <span
                              title={pilar ? `${dr.d} (${pilar})` : dr.d}
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: C.text2,
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {dr.d}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: isNeg ? C.red : C.green,
                                flexShrink: 0,
                              }}
                            >
                              {net > 0 ? "+" : ""}
                              {net}pp
                            </span>
                          </div>
                          <div
                            style={{
                              height: 4,
                              background: C.surf3,
                              borderRadius: 2,
                              overflow: "hidden",
                              display: "flex",
                            }}
                          >
                            <div
                              style={{
                                width: `${pPos}%`,
                                background: C.green,
                                height: "100%",
                              }}
                            />
                            <div
                              style={{
                                width: `${pNeu}%`,
                                background: "#fbbf24",
                                height: "100%",
                              }}
                            />
                            <div
                              style={{
                                width: `${pNeg}%`,
                                background: C.red,
                                height: "100%",
                              }}
                            />
                          </div>
                        </div>
                      );
                    };

                    const metricReal = metrics.find(
                      (m) => m.name === etapaNome,
                    );
                    const etScore = metricReal?.value ?? 0;
                    const etColor =
                      etScore >= 70
                        ? C.green
                        : etScore >= 50
                          ? C.g700
                          : etScore >= 30
                            ? C.orange
                            : C.red;

                    return (
                      <Card key={etapaNome} style={{ padding: "14px 16px" }}>
                        {/* Header da etapa */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 12,
                            paddingBottom: 10,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: C.text,
                                letterSpacing: "-0.01em",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {etapaNome}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: C.text4,
                                marginTop: 1,
                              }}
                            >
                              {isCes ? "CES" : "CSAT"} · meta{" "}
                              {formatMeta(metricReal?.target, "%")}
                            </div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: etColor,
                                letterSpacing: "-0.02em",
                                lineHeight: 1,
                              }}
                            >
                              {etScore}%
                            </div>
                          </div>
                        </div>
                        {/* Lista de drivers */}
                        {driversEt.length === 0 ? (
                          <div
                            style={{
                              fontSize: 10,
                              color: C.text4,
                              fontStyle: "italic",
                              padding: "6px 0",
                            }}
                          >
                            Sem drivers mapeados.
                          </div>
                        ) : (
                          <>
                            {topPos.length === 0 && topNeg.length === 0 ? (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: C.text4,
                                  fontStyle: "italic",
                                  padding: "6px 0",
                                }}
                              >
                                Nenhum driver com impacto significativo nesta etapa.
                              </div>
                            ) : (
                              <>
                                {topPos.length > 0 && (
                                  <div style={{ marginBottom: 8 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 4,
                                        marginBottom: 5,
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: C.green,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                      }}
                                    >
                                      <ArrowUpRight size={10} /> Mais ajudam
                                    </div>
                                    {topPos.map((dr) => renderMini(dr, false))}
                                  </div>
                                )}
                                {topNeg.length > 0 && (
                                  <div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 4,
                                        marginBottom: 5,
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: C.red,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                      }}
                                    >
                                      <ArrowDownRight size={10} /> Mais atrapalham
                                    </div>
                                    {topNeg.map((dr) => renderMini(dr, true))}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            </Section>
          );
        })()}

        {/* === EVOLUÇÃO POR ETAPA — variação em pp === */}
        {(() => {
          const etapasOrdem = Object.keys(JCFG);
          const etapaAtivaNome = etapasOrdem.includes(etapaHistSel)
            ? etapaHistSel
            : etapasOrdem[0];

          const etapaAtivaMetric = metrics.find(
            (m) => m.name === etapaAtivaNome,
          );

          const driversEtapaHist = driverMetricsMonthly.filter((e) => {
            return e.metric_name === etapaAtivaNome ? e : "";
          });

          // Escopado à etapa ativa (driversEtapaHist), não ao
          // driverMetricsMonthly inteiro — senão o top 6 e as linhas do
          // gráfico ficam sempre os mesmos, independente da etapa escolhida
          // no seletor de pills acima.
          const { driverLinesEt, histEtapaData, topDrivers } =
            buildHistEtapaData(driversEtapaHist, 4);

          const topEtapa = [...driversEtapaHist]
            .sort((a, b) => b.total_mentions - a.total_mentions)
            .slice(0, 6);

          const cfg = JCFG[etapaAtivaNome];

          const isCes = cfg && cfg.tipo === "ces";

          return (
            <Section
              title="Evolução do sentimento por etapa — variação em pp"
              action={
                <span
                  style={{ fontSize: 10, color: C.text4, fontStyle: "italic" }}
                >
                  Δpp em relação ao primeiro mês — top 6 drivers da etapa
                </span>
              }
            >
              <div style={{ padding: "4px 20px 18px" }}>
                {/* Selector de etapa em pills — wrap em múltiplas linhas */}
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    paddingBottom: 10,
                    marginBottom: 12,
                    borderBottom: `1px dashed ${C.border}`,
                  }}
                >
                  {etapasOrdem.map((etn) => {
                    const cfgE = JCFG[etn];
                    const isCesE = cfgE && cfgE.tipo === "ces";
                    const isActive = etn === etapaAtivaNome;
                    return (
                      <button
                        key={etn}
                        onClick={() => setEtapaHistSel(etn)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "6px 12px",
                          borderRadius: 20,
                          border: `1px solid ${isActive ? (isCesE ? C.blue : C.g700) : C.border}`,
                          background: isActive
                            ? isCesE
                              ? C.blueBg
                              : C.greenBg
                            : "transparent",
                          color: isActive
                            ? isCesE
                              ? C.blue
                              : C.g700
                            : C.text3,
                          fontSize: 10,
                          fontWeight: isActive ? 700 : 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          fontFamily: "inherit",
                          transition: "all .15s",
                        }}
                      >
                        {etn}
                        <span
                          style={{
                            fontSize: 8,
                            padding: "1px 5px",
                            borderRadius: 8,
                            background: isActive
                              ? isCesE
                                ? C.blue
                                : C.g700
                              : C.surf3,
                            color: isActive ? "#FFFFFF" : C.text4,
                            fontWeight: 700,
                          }}
                        >
                          {isCesE ? "CES" : "CSAT"}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Header da etapa selecionada */}
                {etapaAtivaMetric && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 10,
                      marginBottom: 10,
                      fontSize: 11,
                      color: C.text3,
                    }}
                  >
                    <span
                      style={{ fontSize: 13, fontWeight: 700, color: C.text }}
                    >
                      {etapaAtivaNome}
                    </span>
                    <span>
                      {isCes ? "CES" : "CSAT"} atual:{" "}
                      <b style={{ color: C.text2 }}>
                        {etapaAtivaMetric.value ?? 0}%
                      </b>
                    </span>
                    <span>
                      Meta:{" "}
                      <b style={{ color: C.text2 }}>
                        {formatMeta(etapaAtivaMetric?.target, "%")}
                      </b>
                    </span>
                    {topEtapa.length > 0 && (
                      <span
                        style={{
                          marginLeft: "auto",
                          color: C.text4,
                          fontStyle: "italic",
                        }}
                      >
                        {topEtapa.length} drivers monitorados
                      </span>
                    )}
                  </div>
                )}

                {topEtapa.length === 0 ? (
                  <div
                    style={{
                      padding: "40px 20px",
                      textAlign: "center",
                      color: C.text4,
                      fontSize: 12,
                      fontStyle: "italic",
                    }}
                  >
                    Sem drivers mapeados para essa etapa.
                  </div>
                ) : (
                  <>
                    {/* Toggle legend */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: 10,
                      }}
                    >
                      {driverLinesEt.map((l) => (
                        <button
                          key={l.key}
                          onClick={() => toggleLine(l.key)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            background: hiddenLines[l.key]
                              ? C.surf3
                              : "transparent",
                            border: `1px solid ${hiddenLines[l.key] ? C.border : l.stroke}`,
                            borderRadius: 20,
                            padding: "3px 10px",
                            cursor: "pointer",
                            opacity: hiddenLines[l.key] ? 0.35 : 1,
                            transition: "all .2s",
                            fontFamily: "inherit",
                          }}
                        >
                          <div
                            style={{
                              width: 12,
                              height: 2,
                              borderRadius: 1,
                              background: l.stroke,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 10,
                              color: hiddenLines[l.key] ? C.text4 : C.text,
                              fontWeight: 500,
                            }}
                          >
                            {l.name}
                          </span>
                        </button>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={histEtapaData}
                        margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={C.border}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="mes"
                          tick={{ fontSize: 11, fill: C.text2 }}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: C.text4 }}
                          tickFormatter={(v) => (v > 0 ? "+" : "") + v + "pp"}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !(payload && payload.length))
                              return null;
                            return (
                              <div
                                style={{
                                  background: "rgba(28,28,30,.95)",
                                  backdropFilter: "blur(12px)",
                                  borderRadius: 10,
                                  padding: "10px 13px",
                                  fontSize: 11,
                                  boxShadow: "0 8px 24px rgba(0,0,0,.2)",
                                  border: "1px solid rgba(255,255,255,.1)",
                                }}
                              >
                                <div
                                  style={{
                                    color: C.g300,
                                    fontWeight: 600,
                                    marginBottom: 6,
                                    fontSize: 11,
                                  }}
                                >
                                  {label}/26 — variação acumulada
                                </div>
                                {payload.map((p, idx) => {
                                  const dl = driverLinesEt.find(
                                    (l) => l.key === p.dataKey,
                                  );
                                  if (!dl) return null;
                                  const pilar = getPilarOf(dl.essence, dl.metricCategory);
                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        color: "#FFFFFF",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 3,
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: 8,
                                          height: 2,
                                          background: dl.stroke,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ fontSize: 10 }}>
                                          {dl.name}
                                        </span>
                                        {pilar && (
                                          <span
                                            style={{
                                              fontSize: 9,
                                              color: PILLAR_COLORS[pilar],
                                              marginLeft: 5,
                                              fontWeight: 600,
                                            }}
                                          >
                                            ({pilar})
                                          </span>
                                        )}
                                      </div>
                                      <b
                                        style={{
                                          color:
                                            p.value > 0
                                              ? "#4ade80"
                                              : p.value < 0
                                                ? "#f87171"
                                                : "#FFFFFF",
                                          fontSize: 11,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {p.value > 0 ? "+" : ""}
                                        {p.value}pp
                                      </b>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }}
                        />
                        <ReferenceLine
                          y={0}
                          stroke={C.text3}
                          strokeDasharray="3 3"
                          strokeWidth={1.5}
                          label={{
                            value: "Baseline",
                            position: "right",
                            fontSize: 10,
                            fill: C.text4,
                          }}
                        />
                        {driverLinesEt.map((l) => (
                          <Line
                            isAnimationActive={animarGraficos}
                            key={l.key}
                            type="monotone"
                            dataKey={l.key}
                            name={l.name}
                            hide={!!hiddenLines[l.key]}
                            stroke={l.stroke}
                            strokeWidth={2}
                            dot={{
                              fill: l.stroke,
                              r: hiddenLines[l.key] ? 0 : 3,
                              strokeWidth: 0,
                            }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                    <div
                      style={{
                        marginTop: 8,
                        padding: "8px 12px",
                        background: C.surf3,
                        borderRadius: 8,
                        fontSize: 10,
                        color: C.text4,
                        fontStyle: "italic",
                      }}
                    >
                      O gráfico mostra{" "}
                      <b style={{ color: C.text3 }}>
                        variação em pp em relação ao primeiro mês (Out)
                      </b>
                      , não o net sentiment absoluto. Linhas acima de zero =
                      drivers que melhoraram desde o início; linhas abaixo =
                      drivers que pioraram. Útil pra comparar quem está
                      acelerando ou desacelerando dentro de uma mesma etapa.
                    </div>
                  </>
                )}
              </div>
            </Section>
          );
        })()}

        {/* === EVOLUÇÃO HISTÓRICA DA DISTRIBUIÇÃO DE SENTIMENTO === */}
        {(() => {
          const driverLinesTemp = transformarDriverLines(driverMonthly);
          const driverLines = driverLinesTemp.lines;

          const histData = transformarHistData(
            driverMonthly,
            driverLinesTemp.driverMap,
            driverLinesTemp.topDrivers,
          );

          return (
            <Section
              title="Evolução da distribuição de sentimento por driver"
              action={
                <span
                  style={{ fontSize: 10, color: C.text4, fontStyle: "italic" }}
                >
                  Net sentiment (positivo − negativo) — top 6 drivers por volume
                </span>
              }
            >
              <div style={{ padding: "0 20px 18px" }}>
                {/* Toggle legend */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginBottom: 10,
                    paddingTop: 8,
                  }}
                >
                  {driverLines.map((l) => (
                    <button
                      key={l.key}
                      onClick={() => toggleLine(l.key)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        background: hiddenLines[l.key]
                          ? C.surf3
                          : "transparent",
                        border: `1px solid ${hiddenLines[l.key] ? C.border : l.stroke}`,
                        borderRadius: 20,
                        padding: "3px 10px",
                        cursor: "pointer",
                        opacity: hiddenLines[l.key] ? 0.35 : 1,
                        transition: "all .2s",
                        fontFamily: "inherit",
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 2,
                          borderRadius: 1,
                          background: l.stroke,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 10,
                          color: hiddenLines[l.key] ? C.text4 : C.text,
                          fontWeight: 500,
                        }}
                      >
                        {l.name}
                      </span>
                    </button>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart
                    data={histData}
                    margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 11, fill: C.text2 }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: C.text4 }}
                      tickFormatter={(v) => v + "pp"}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !(payload && payload.length))
                          return null;
                        return (
                          <div
                            style={{
                              background: "rgba(28,28,30,.95)",
                              backdropFilter: "blur(12px)",
                              borderRadius: 10,
                              padding: "10px 13px",
                              fontSize: 11,
                              boxShadow: "0 8px 24px rgba(0,0,0,.2)",
                              border: "1px solid rgba(255,255,255,.1)",
                            }}
                          >
                            <div
                              style={{
                                color: C.g300,
                                fontWeight: 600,
                                marginBottom: 6,
                                fontSize: 11,
                              }}
                            >
                              {label}/26 — saldo (elogios − reclamações)
                            </div>
                            {payload.map((p, idx) => {
                              const dl = driverLines.find(
                                (l) => l.key === p.dataKey,
                              );
                              if (!dl) return null;
                              const pilar = getPilarOf(dl.essence, dl.metricCategory);
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    color: "#FFFFFF",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginBottom: 3,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 8,
                                      height: 2,
                                      background: dl.stroke,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ fontSize: 10 }}>
                                      {dl.name}
                                    </span>
                                    {pilar && (
                                      <span
                                        style={{
                                          fontSize: 9,
                                          color: PILLAR_COLORS[pilar],
                                          marginLeft: 5,
                                          fontWeight: 600,
                                        }}
                                      >
                                        ({pilar})
                                      </span>
                                    )}
                                  </div>
                                  <b
                                    style={{
                                      color:
                                        p.value > 0
                                          ? "#4ade80"
                                          : p.value < 0
                                            ? "#f87171"
                                            : "#FFFFFF",
                                      fontSize: 11,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {p.value > 0 ? "+" : ""}
                                    {p.value}pp
                                  </b>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                    <ReferenceLine
                      y={0}
                      stroke={C.text4}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                    {driverLines.map((l) => (
                      <Line
                        isAnimationActive={animarGraficos}
                        key={l.key}
                        type="monotone"
                        dataKey={l.key}
                        name={l.name}
                        hide={!!hiddenLines[l.key]}
                        stroke={l.stroke}
                        strokeWidth={2}
                        dot={{
                          fill: l.stroke,
                          r: hiddenLines[l.key] ? 0 : 3,
                          strokeWidth: 0,
                        }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 12px",
                    background: C.surf3,
                    borderRadius: 8,
                    fontSize: 10,
                    color: C.text4,
                    fontStyle: "italic",
                  }}
                >
                  O <b style={{ color: C.text3 }}>net sentiment</b> compara o
                  quanto cada driver foi mencionado positivamente vs
                  negativamente. Valores acima de 0 = drivers com elogios
                  líquidos; abaixo de 0 = drivers com atrito líquido. Salto
                  entre meses indica mudança real na percepção do beneficiário.
                </div>
              </div>
            </Section>
          );
        })()}
      </div>
    );
  })();
}
